import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errorText, useFetch } from "../api";
import { Alerts, Badge, ConfirmDialog, EmptyRow, Loading, Page, Pager, ReasonDialog, Tabs, UserLink } from "../components/ui";
import { PAGE_SIZE, qs, setQuery, text, when } from "../format";

function ClaimActions({ id, onDone }) {
  const [mode, setMode] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState({ message: "", errors: {} });

  function close() {
    if (busy) return;
    setMode("");
    setProblem({ message: "", errors: {} });
  }

  async function review(status, note) {
    setBusy(true);
    setProblem({ message: "", errors: {} });
    const body = { status };
    if (note) body.note = note;
    try {
      await api("/reward-claims/" + id + "/review", { method: "POST", body });
      setMode("");
      onDone(status === "approved" ? "Claim approved" : "Claim rejected");
    } catch (err) {
      setProblem({ message: errorText(err), errors: err.errors || {} });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row-actions">
      <button type="button" className="btn small" onClick={() => setMode("approve")}>Approve</button>
      <button type="button" className="btn danger small" onClick={() => setMode("reject")}>Reject</button>
      {mode === "approve" ? (
        <ConfirmDialog
          title="Approve claim"
          text="This does not add money to a wallet."
          error={problem.message}
          busy={busy}
          confirmLabel="Approve"
          onClose={close}
          onConfirm={() => review("approved")}
        />
      ) : null}
      {mode === "reject" ? (
        <ReasonDialog
          title="Reject claim"
          label="Note"
          hint="Required. The member can see this note."
          error={problem.errors.note || problem.message}
          busy={busy}
          onClose={close}
          onSubmit={(note) => review("rejected", note)}
        />
      ) : null}
    </div>
  );
}

export default function RewardClaims() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, Number(params.get("page")) || 1);
  const status = params.get("status") || "pending";
  const list = useFetch("/reward-claims" + qs({ page, limit: PAGE_SIZE, status }));
  const rows = Array.isArray(list.data?.data) ? list.data.data : [];
  const [notice, setNotice] = useState("");

  return (
    <Page title="Claims" text="Review a reward claim. Approval does not add wallet money.">
      <Alerts error={list.error} notice={notice} />
      <Tabs
        value={status}
        onChange={(value) => {
          setNotice("");
          setQuery(params, setParams, { status: value, page: "" });
        }}
        options={[
          { value: "pending", label: "Pending" },
          { value: "approved", label: "Approved" },
          { value: "rejected", label: "Rejected" },
        ]}
      />
      {list.loading ? <Loading /> : (
        <div className="table-wrap">
          <table className="wide">
            <thead>
              <tr>
                <th>Member</th><th>Reward</th><th>Directs</th><th>Status</th><th>Note</th><th>Claimed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row) => (
                <tr key={row.id}>
                  <td><UserLink user={row.user} /></td>
                  <td>{row.reward ? row.reward.title : "—"}</td>
                  <td>{row.reward ? row.reward.requiredDirects : "—"}</td>
                  <td><Badge value={row.status} /></td>
                  <td>{text(row.note)}</td>
                  <td>{when(row.createdAt)}{row.reviewedAt ? <div className="hint">Reviewed {when(row.reviewedAt)}</div> : null}</td>
                  <td>{row.status === "pending" ? <ClaimActions id={row.id} onDone={(message) => { setNotice(message); list.reload(); }} /> : null}</td>
                </tr>
              )) : <EmptyRow cols={7} text="No claims" />}
            </tbody>
          </table>
        </div>
      )}
      {list.data ? (
        <Pager page={page} limit={PAGE_SIZE} total={list.data.total} onPage={(next) => setQuery(params, setParams, { page: next <= 1 ? "" : next })} />
      ) : null}
    </Page>
  );
}
